import { Fragment, Dispatch, SetStateAction } from 'react';
import styled, { css } from 'styled-components';
import {
    HiChevronDoubleLeft,
    HiChevronDoubleRight,
    HiChevronLeft,
    HiChevronRight
} from 'react-icons/hi2';
import { darken } from 'polished';

import theme from '@styles/theme';

type PaginationProps = {
    BORDER?: boolean;
    pageGroupLength: number;
    pageIndex: number;
    take: number;
    totalLength: number;
    setPageIndex: Dispatch<SetStateAction<number>>;
    prevPageHandler: () => void;
    nextPageHandler: () => void;
    prevPageGroupHandler: () => void;
    nextPageGroupHandler: () => void;
    resetPaginationHandler: () => void;
    disabled: Record<string, boolean>;
    margin?: string;
    padding?: string;
};

const Pagination = ({
    BORDER = false,
    pageGroupLength,
    pageIndex,
    take,
    totalLength,
    setPageIndex,
    prevPageHandler,
    nextPageHandler,
    prevPageGroupHandler,
    nextPageGroupHandler,
    resetPaginationHandler,
    disabled,
    margin = '12px 0 0',
    padding = '20px 0 0'
}: PaginationProps) => {
    return (
        <Wrapper $margin={margin} $padding={padding} $border={BORDER}>
            {/* 이동 버튼 영역 */}
            <Container $gap={4}>
                <Button
                    onClick={prevPageGroupHandler}
                    disabled={disabled.prevPageGroup}
                >
                    <HiChevronDoubleLeft
                        color={`${theme.colors.lightGrayFontColor}`}
                        size={20}
                    />
                </Button>
                <Button onClick={prevPageHandler} disabled={disabled.prevPage}>
                    <HiChevronLeft
                        color={`${theme.colors.lightGrayFontColor}`}
                        size={20}
                    />
                </Button>
                <ListContainer>
                    {[...Array(5)].map((_, i) => (
                        <Fragment key={pageGroupLength / take + 1 + i}>
                            {pageGroupLength / take + 1 + i <=
                                Math.ceil(totalLength / take) && (
                                <List
                                    onClick={() => {
                                        setPageIndex(i * take);
                                    }}
                                    $active={i === pageIndex / take}
                                >
                                    {pageGroupLength / take + 1 + i}
                                </List>
                            )}
                        </Fragment>
                    ))}
                </ListContainer>
                <Button onClick={nextPageHandler} disabled={disabled.nextPage}>
                    <HiChevronRight
                        color={`${theme.colors.lightGrayFontColor}`}
                        size={20}
                    />
                </Button>
                <Button
                    onClick={nextPageGroupHandler}
                    disabled={disabled.nextPageGroup}
                >
                    <HiChevronDoubleRight
                        color={`${theme.colors.lightGrayFontColor}`}
                        size={20}
                    />
                </Button>
            </Container>
        </Wrapper>
    );
};

export default Pagination;

const Wrapper = styled.div<{
    $margin?: string;
    $padding?: string;
    $border?: boolean;
}>`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: ${({ $margin }) => $margin};
    padding: ${({ $padding }) => $padding};
    width: 100%;

    ${({ $border }) =>
        $border &&
        css`
            border-top: ${theme.colors.ultraLightGrayBorder} 1px solid;
        `}
`;
const Container = styled.div<{ $gap?: number }>`
    position: relative;
    display: flex;
    align-items: center;
    font-family: PretendardRegular, sans-serif;

    ${({ $gap }) =>
        $gap &&
        css`
            gap: ${$gap}px;
        `}
`;
const Button = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    border: ${theme.colors.ultraLightGrayBorder} solid 1px;
    background-color: ${theme.colors.whiteColor};
    border-radius: 4px;
    &:hover {
        background-color: ${darken(0.01, theme.colors.whiteColor)};
    }
    &:active {
        background-color: ${darken(0.02, theme.colors.whiteColor)};
    }
    &:disabled {
        background-color: ${darken(0.03, theme.colors.whiteColor)};
        cursor: auto;
    }
`;
const ListContainer = styled.ul`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    padding: 0 16px;
`;
const List = styled.li<{ $active?: boolean }>`
    width: 100%;
    color: ${theme.colors.sidebarGrayFontColor};
    font-size: 14px;
    cursor: pointer;
    transition: all 0.25s;
    &:hover {
        /* color: ${theme.colors.blackColor}; */
        color: ${({ $active }) =>
            !$active
                ? darken(0.3, theme.colors.sidebarGrayFontColor)
                : theme.colors.blackColor};
    }
    ${({ $active }) =>
        $active &&
        css`
            color: ${theme.colors.blackColor};
        `}
`;
